import posthog from 'posthog-js';
import eventBus from '@app2/core/eventBus';
import router from '@app2/router';
import debounce from 'lodash/debounce';
import { normalizeEmail } from '@app2/utils/helpers';

const debouncer = debounce(cb => cb(), 100);

// Initialize PostHog and set up event listeners immediately
let posthogInitialized = false;

const initializePosthog = () => {
    if (posthogInitialized) return;

    posthog.init(process.env.MIX_POSTHOG_KEY, {
        api_host: 'https://app.posthog.com',
        // Share the anonymous identity cookie across *.membership.io so blog
        // (membership.io) readers stitch to their signup here. This preserves the
        // anonymous $device_id that links pre-signup pageviews to the account.
        // NOTE: we intentionally do NOT bootstrap distinctID from a custom cookie —
        // that set $device_id to an email and broke blog → signup attribution.
        cross_subdomain_cookie: true,
        autocapture: {
            dom_event_allowlist: ['click','submit'], // DOM events from this list ['click', 'change', 'submit']
            element_allowlist: ['button', 'a', 'form', 'label', 'select'], // DOM elements from this list ['a', 'button', 'form', 'input', 'select', 'textarea', 'label']
        },
        session_recording: {
            maskAllInputs: true,
        }
    });

    posthogInitialized = true;
};

// Set up event listeners immediately when module loads
const setupEventListeners = () => {
    eventBus.$on('posthog:stop-recording', () => {
        if (posthogInitialized) {
            posthog.stopSessionRecording();
        }
    });

    eventBus.$on('posthog:start-recording', () => {
        if (posthogInitialized) {
            posthog.startSessionRecording();
        }
    });

    eventBus.$on('posthog:page-view', () => {
        if (posthogInitialized) {
            debouncer(() => {
                posthog.capture('$pageview');
            });
        }
    });

    eventBus.$on('posthog:claim-hub', (data) => {
        initializePosthog(); // Initialize PostHog if not already done
        
        debouncer(() => {
            // If email is provided, identify the user immediately before capturing.
            // Do NOT reset() — that discards the anonymous $device_id carried over
            // from the blog (membership.io) and breaks blog → signup attribution.
            if (data && data.email) {
                const phEmail = normalizeEmail(data.email);
                if (phEmail) {
                    posthog.identify(phEmail, { email: phEmail });
                }
            }
            posthog.capture('claim_hub');
        });
    });

    eventBus.$on('posthog:launch-hub', (data) => {
        initializePosthog(); // Initialize PostHog if not already done
        
        debouncer(() => {
            // If email is provided, identify the user immediately before capturing.
            // Do NOT reset() — that discards the anonymous $device_id carried over
            // from the blog (membership.io) and breaks blog → signup attribution.
            if (data && data.email) {
                const phEmail = normalizeEmail(data.email);
                if (phEmail) {
                    posthog.identify(phEmail, { email: phEmail });
                }
            }
            posthog.capture('launch_hub');
        });
    });

    eventBus.$on('posthog:added-payment-details', (data) => {
        initializePosthog(); // Initialize PostHog if not already done
        
        debouncer(() => {
            // If email is provided, identify the user immediately before capturing.
            // Do NOT reset() — that discards the anonymous $device_id carried over
            // from the blog (membership.io) and breaks blog → signup attribution.
            if (data && data.email) {
                const phEmail = normalizeEmail(data.email);
                if (phEmail) {
                    posthog.identify(phEmail, { email: phEmail });
                }
            }
            posthog.capture('added_payment_details');
        });
    });
};

// Set up listeners immediately when module is imported
setupEventListeners();

export const registerPosthog = (user) => {
    initializePosthog();

    // Initialize user properties with basic information
    const userProperties = {
        email: normalizeEmail(user.email),
        name: String(user.name),
        created_at: String(user.created_at),
        team_id: user.current_team_id,
    };
    
    // Fetch additional fields if chargebee_current_plan isn't null
    if (user.chargebee_current_plan) {
        userProperties.chargebee_item_id = String(user.chargebee_current_plan.item_id);
        userProperties.chargebee_item_price_id = String(user.chargebee_current_plan.item_price_id);
        userProperties.chargebee_status = String(user.chargebee_current_plan.status);
    }

    // Identify the user with the specified properties. distinct_id is the
    // normalized email so it matches the blog and the backend PostHogService.
    // Guard against a blank email so we never identify() with an empty id,
    // which would collapse such users into a single PostHog person.
    if (userProperties.email) {
        posthog.identify(userProperties.email, userProperties);
    }

    if ( !router.currentRoute.meta.ph ) {
        posthog.stopSessionRecording();
    }
};